import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const STEPS = [
  {
    n: "01",
    title: "Free Assessment",
    body: "15 minutes to size your exposure. No commitment.",
  },
  {
    n: "02",
    title: "Entry Audit",
    body: "We review your import history and identify every IEEPA entry.",
  },
  {
    n: "03",
    title: "CAPE Filing",
    body: "We prepare and submit your complete CBP claim.",
  },
  {
    n: "04",
    title: "Refund or Advance",
    body: "CBP processes in 60–90 days — or take funding now.",
  },
  {
    n: "05",
    title: "We Collect",
    body: "Our fee only applies on successful recovery.",
  },
];

export default function Process() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const height = useTransform(scrollYProgress, [0.1, 0.85], ["0%", "100%"]);

  return (
    <section
      id="process"
      data-testid="process-section"
      ref={ref}
      className="relative py-24 sm:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mb-20"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#0A1936]" />
            <span className="font-mono uppercase tracking-[0.24em] text-[11px] text-[#0A1936]/70">
              04 · The Process
            </span>
          </div>
          <h2 className="font-serif text-[#0A1936] text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
            Simple. Fast.
            <br />
            <span className="italic">Risk-free.</span>
          </h2>
        </motion.div>

        <div className="relative">
          {/* Vertical timeline rail */}
          <div className="absolute left-[27px] md:left-[45px] top-0 bottom-0 w-px bg-[#0A1936]/10" />
          <motion.div
            style={{ height }}
            className="absolute left-[27px] md:left-[45px] top-0 w-px bg-[#F5B800] origin-top"
          />

          <div className="space-y-14">
            {STEPS.map((step, i) => (
              <motion.div
                key={step.n}
                data-testid={`process-step-${i + 1}`}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.7, delay: i * 0.05 }}
                className="relative pl-20 md:pl-32"
              >
                {/* Numbered dot */}
                <div className="absolute left-0 top-0 w-[56px] md:w-[92px] flex items-center justify-start">
                  <div className="relative flex items-center justify-center w-14 h-14 md:w-[92px] md:h-[92px] rounded-full bg-white border border-[#0A1936]/15">
                    <span className="font-serif italic text-[#F5B800] text-lg md:text-2xl">
                      {step.n}
                    </span>
                  </div>
                </div>

                <div className="pt-3 md:pt-6">
                  <h3 className="font-serif text-[#0A1936] text-2xl sm:text-3xl lg:text-4xl leading-tight">
                    {step.title}
                  </h3>
                  <p className="mt-3 text-[#0A1936]/70 leading-relaxed max-w-xl">
                    {step.body}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
